import { FC } from "react";
import {
  MuiDoubleLabel,
  MuiGrid,
  MuiViewMorePopOver,
  OptionsDataType,
} from "../../../../../../../../../../components";
import { viewMorePopOverGridSize } from "../../../../../../../../../../helpers/constants";
import { getFieldRuleByCountryForLandingInformation } from "../../../../../../../../../../helpers/constants/countryFieldRulesUtils";
import {
  convertToUserFormatWithLocalTime,
  getNumberValueAsStringOrBlank,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import {
  EmployerInformationType,
  GeneralInformationType,
  ProgramClosureFormKey,
} from "../../../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../../siteCore/SiteCoreKeys";

interface GeneralInformationProps {
  pageId: string;
  userCountryDateFormat?: string;
  generalInformation?: GeneralInformationType;
  employerInformation?: EmployerInformationType;
  countryList: OptionsDataType[];
  deliveryCountry?: string;
}

export const GeneralInformation: FC<GeneralInformationProps> = ({
  pageId,
  userCountryDateFormat,
  generalInformation,
  employerInformation,
  countryList,
  deliveryCountry = "",
}) => {
  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const getCountryDisplayValueFromList = (country: string) => {
    if (isEmpty(country)) {
      return country;
    }
    const countryData = countryList?.find(
      (data) => data.value.toString().toLowerCase() === country.toLowerCase(),
    );
    return countryData?.label;
  };

  const geographicMoveRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.geographicalMove,
  );

  const landingCountryRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.landingCountry,
  );

  const stateRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.state,
  );

  return (
    <>
      <MuiGrid
        container
        className="form-container grid-container"
        data-testid={`${pageId}-general-information`}
      >
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.landedDate)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={
              generalInformation?.landedDate
                ? convertToUserFormatWithLocalTime(
                    generalInformation.landedDate,
                    userCountryDateFormat,
                  )
                : ""
            }
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.timeToLand)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={getNumberValueAsStringOrBlank(
              generalInformation?.timeToLandInDays,
            )}
          />
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container className="form-container grid-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.totalNumberOfMeetings)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={getNumberValueAsStringOrBlank(
              generalInformation?.totalMeetings,
            )}
          />
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container className="form-container grid-container">
        {geographicMoveRule.visible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.geographicMove)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                programSiteCoreKeys.changeStatusModelPopupKey,
                programSiteCoreKeys.geographicMoveList,
                getValueOrBlank(generalInformation?.geogrphicMove),
              )}
            />
          </MuiGrid>
        )}
        {landingCountryRule.visible && (
          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            data-testid="landing-country"
          >
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.landingCountry)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={getCountryDisplayValueFromList(
                getValueOrBlank(generalInformation?.landingCountry),
              )}
            />
          </MuiGrid>
        )}
        {stateRule.visible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.stateProvinceRegion)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={getValueOrBlank(employerInformation?.state)}
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.landedInformationPostalCode)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={getValueOrBlank(employerInformation?.postalCode)}
          />
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container className="form-container grid-container">
        <MuiGrid size={viewMorePopOverGridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={commonLabels.notes}
            replaceEmptyWithDash
            value={
              isNotEmpty(generalInformation?.notes) ? (
                <MuiViewMorePopOver
                  anchorText={commonLabels.viewMore}
                  message={getValueOrBlank(generalInformation?.notes)}
                  placement="bottom-end"
                />
              ) : (
                ""
              )
            }
          />
        </MuiGrid>
      </MuiGrid>
    </>
  );
};
